import { Mail, Phone, MapPin, Instagram, Facebook, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { openCookieSettings } from '@/lib/consent';

const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer className="bg-[#101B2D] text-white pt-20 pb-12 border-t border-[#C5A059]/20" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Logo e Descrição */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img 
                src="/fechadura.png" 
                alt="Pousada do Mondego" 
                className="w-12 h-16"
              />
              <h2 className="text-xl font-bold tracking-tight uppercase">Pousada do Mondego</h2>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a
                href="https://wa.me/5531991640648"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#25D366] transition-colors group"
                data-testid="footer-whatsapp-link"
                title="WhatsApp"
              >
                <MessageCircle className="w-5 h-5 group-hover:fill-white" />
              </a>
              <a
                href="https://instagram.com/pousadadomondego"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#C5A059] transition-colors"
                data-testid="footer-instagram-link"
                title="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com/pousadadomondego"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#C5A059] transition-colors"
                data-testid="footer-facebook-link"
                title="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links Rápidos */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059] mb-6">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/suites" className="text-sm text-gray-400 hover:text-[#C5A059] transition-colors" data-testid="footer-link-suites">
                  {t('nav.suites')}
                </Link>
              </li>
              <li>
                <Link to="/historia" className="text-sm text-gray-400 hover:text-[#C5A059] transition-colors" data-testid="footer-link-historia">
                  {t('nav.history')}
                </Link>
              </li>
              <li>
                <Link to="/experiencias" className="text-sm text-gray-400 hover:text-[#C5A059] transition-colors" data-testid="footer-link-experiencias">
                  {t('nav.experiences')}
                </Link>
              </li>
              <li>
                <Link to="/galeria" className="text-sm text-gray-400 hover:text-[#C5A059] transition-colors" data-testid="footer-link-galeria">
                  {t('nav.gallery')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Informações */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059] mb-6">
              {t('footer.information')}
            </h3>
            <ul className="space-y-3">
              <li className="text-sm text-gray-400">{t('contact.checkIn')}</li>
              <li className="text-sm text-gray-400">{t('contact.checkOut')}</li>
              <li className="text-sm text-gray-400">{t('contact.breakfastIncluded')}</li>
              <li className="text-sm text-gray-400">{t('contact.freeWifi')}</li>
              <li className="text-sm text-gray-400">{t('contact.freeParking')}</li>
            </ul>
          </div>

          {/* Contato */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059] mb-6">
              {t('nav.contact')}
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-400">
                  Largo de Coimbra, 38<br />
                  Ouro Preto, MG<br />
                  35400-000
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#C5A059]" />
                <a href="tel:+553135527700" className="text-sm text-gray-400 hover:text-[#C5A059] transition-colors" data-testid="footer-phone">
                  +55 31 3552-7700
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#C5A059]" />
                <a href="mailto:reservas@mondego.com.br" className="text-sm text-gray-400 hover:text-[#C5A059] transition-colors" data-testid="footer-email">
                  reservas@mondego.com.br
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-8 border-t border-gray-800 text-center">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Pousada do Mondego. {t('footer.copyright')}
          </p>
          <button
            type="button"
            onClick={openCookieSettings}
            className="mt-2 text-sm text-gray-500 underline-offset-4 transition-colors hover:text-[#C5A059] hover:underline"
            data-testid="footer-cookie-settings"
          >
            {t('cookies.settings')}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
